/*eslint-disable*/
import {
  IBenefit,
  IBenefitAgePrice,
  IBenefitFixedPrice,
  IBenefitLimitedPrice,
  IBenefitListTable,
  IBenefitType,
  IBenefitsConfigVersion,
  IBenefitsVersionManagement,
  IEnrollmentEvent,
  INewHireConfig,
  IPagination,
  IPremiumPayoutConfig,
  IBenefitsDocument,
  IQleNewHireEnrollmentEvent,
} from 'src/interfaces';
import axios, { AxiosError, AxiosResponse } from 'axios';
import http from './http';
import { getStorageValue } from 'src/hooks/useLocalStorage';

export type PriceFormType = IBenefitAgePrice & IBenefitFixedPrice;
export type DocumentsFormType = IBenefitsDocument;

type CreateNewEnrollmentEvent = IEnrollmentEvent & { companyId: string };
export interface IEnrollmentEventsWithPagination {
  [x: string]: string | number | boolean | undefined;
  page: number;
  limit: number;
  companyId: string;
}

export interface IGetBenefitsWithParams extends IPagination {
  [x: string]: string | number | boolean | undefined;
  sortBy?: string;
  sortDirection?: string;
  companyId: string;
}

export interface IGetBenefitsVersionWithParams extends IPagination {
  [x: string]: string | number | boolean | undefined;
  sortBy?: string;
  sortDirection?: string;
  companyId: string;
}

export type WorkerEnrolledBenefitsType = {
  benefitId: string;
  workerId: string;
  enrollmentStart: string;
  enrollmentEnd: string;
  calculateRate: string;
  amountOrRateContribution: number;
  basisDeduction: string;
  basisContributions: string;
  amountOrRateDeduction: number;
  effective: string;
  expiration: string;
  employeeClass: string;
  submissionDate: string;
  firstName: string;
  lastName: string;
  planName: string;
  planDescription: string;
  eventType: string;
  ssn: string;
};

class BenefitsService {
  static editBenefit(data: FormData) {
    return http
      .patch('/benefits', data)
      .then(response => response.data)
      .catch((err: Error | AxiosError) => {
        if (axios.isAxiosError(err)) {
          throw new Error(err.response?.data?.message);
        } else {
          return err;
        }
      });
  }

  static planDocuments(data: FormData) {
    return http
      .post('/plan-documents', data)
      .then(response => response.data)
      .catch((err: Error | AxiosError) => {
        if (axios.isAxiosError(err)) {
          throw new Error(err.response?.data?.message);
        } else {
          return err;
        }
      });
  }

  static getBenefits(
    params: IGetBenefitsWithParams
  ): Promise<{ items: IBenefitListTable[]; meta: { totalItems: number } }> {
    let stringParams = '';
    // eslint-disable-next-line no-restricted-syntax
    for (const key in params) {
      if (stringParams === '') {
        stringParams += `${key}=${params[key]}`;
      } else {
        stringParams += `&${key}=${params[key]}`;
      }
    }
    return http
      .get(`benefits?${stringParams}`, {
        headers: { companyId: params.companyId },
      })
      .then(resp => resp.data);
  }

  static getBenefitsByEmployee(
    personnelNumber: string,
    params: IGetBenefitsWithParams
  ): Promise<{ items: IBenefitListTable[]; meta: { totalItems: number } }> {
    let stringParams = '';
    // eslint-disable-next-line no-restricted-syntax
    for (const key in params) {
      if (stringParams === '') {
        stringParams += `${key}=${params[key]}`;
      } else {
        stringParams += `&${key}=${params[key]}`;
      }
    }
    return http
      .get(`/employee/benefits/${personnelNumber}?${stringParams}`, {
        headers: { companyId: params.companyId },
      })
      .then(resp => resp.data);
  }

  static getBenefitTypeByVersionId(id: string): Promise<IBenefitType> {
    return http.get(`/benefits/types/${encodeURIComponent(id)}`).then(resp => resp.data);
  }

  static getBenefitFixedPrice(params: { versionId: string; search: string }): Promise<{ items: IBenefitFixedPrice[] }> {
    if (params.search) {
      return http
        .get(`/benefits/prices/fixed-prices?versionId=${params.versionId}&search=${params.search}`)
        .then(resp => resp.data);
    }
    return http.get(`/benefits/prices/fixed-prices?versionId=${params.versionId}`).then(resp => resp.data);
  }

  static getBenefitAgePrice(params: { type: string; search: string }): Promise<{ items: IBenefitAgePrice[] }> {
    if (params.search) {
      return http
        .get(`/benefits/prices/age-prices?type=${params.type}&search=${params.search}`)
        .then(resp => resp.data);
    }
    return http.get(`/benefits/prices/age-prices?type=${params.type}`).then(resp => resp.data);
  }

  static getBenefitPrices(params: { versionId: string; search: string }): Promise<PriceFormType[]> {
    if (params.search) {
      return http
        .get(`/benefits/prices?versionId=${encodeURIComponent(params.versionId)}&search=${params.search}`)
        .then(resp => resp.data);
    }
    return http.get(`/benefits/prices?versionId=${encodeURIComponent(params.versionId)}`).then(resp => resp.data);
  }

  static getBenefitLimitedPrices(params: { versionId: string }): Promise<IBenefitLimitedPrice[]> {
    return http
      .get(`/prices/limited?versionId=${encodeURIComponent(params.versionId)}`)
      .then(resp => resp.data)
      .catch((err: Error | AxiosError) => {
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          return [];
        }
        throw err;
      });
  }

  static patchBenefitLimitedPrices(data: IBenefitLimitedPrice[]) {
    return http
      .patch('/prices/limited', data)
      .then(response => response.data)
      .catch((err: Error | AxiosError) => {
        if (axios.isAxiosError(err)) {
          throw new Error(err.response?.data?.message);
        } else {
          return err;
        }
      });
  }

  static getPlanDocumentsTypes(type: any): Promise<AxiosResponse> {
    return http.get(`/plan-documents/${type}`);
  }

  static getPlanDocument(versionId: string): Promise<DocumentsFormType[]> {
    return http.get(`/plan-documents/documents/${versionId}`).then(resp => resp.data);
  }

  static deletePlanDocument(value: string): Promise<void> {
    return http.delete(`/plan-documents/${value}`);
  }

  static downloadPlanDocument(id: string) {
    return `${process.env.REACT_APP_API_URL}plan-documents/download/${id}?token=${getStorageValue(
      'accessToken',
      null
    )}`;
  }

  static deleteBenefitPrices(params: { type: string; id: string }): Promise<void> {
    return http.delete(`/benefits/prices?priceType=${params.type}&headerId=${params.id}`).then(resp => resp.data);
  }

  static getBenefitsTypes(searchTerm?: string): Promise<IBenefitType[]> {
    if (searchTerm) {
      return http.get(`/benefits/types?search=${searchTerm}`).then(resp => resp.data);
    }
    return http.get('/benefits/types').then(resp => resp.data);
  }

  static getBenefitsImgGallery(): Promise<[{ key: string; url: string }]> {
    return http.get('/benefits/images').then(resp => resp.data);
  }

  static getEnrollmentEvents(params: IEnrollmentEventsWithPagination): Promise<{
    data: { items: IEnrollmentEvent[]; meta: { totalItems: number } };
  }> {
    let stringParams = '';
    // eslint-disable-next-line no-restricted-syntax
    for (const key in params) {
      if (stringParams === '') {
        stringParams += `${key}=${params[key]}`;
      } else {
        stringParams += `&${key}=${params[key]}`;
      }
    }

    return http.get(`benefits/enrollment-events?${stringParams}`, {
      headers: { companyId: params.companyId },
    });
  }

  static createNewEnrollmentEvent(data: CreateNewEnrollmentEvent): Promise<{ id: string }> {
    return http
      .post('/benefits/enrollment-events', data, {
        headers: { companyId: data.companyId },
      })
      .then(response => response.data);
  }

  static getWorkerEnrolledBenefits(params: IGetBenefitsWithParams): Promise<{
    items: WorkerEnrolledBenefitsType[];
    meta: { totalItems: number };
  }> {
    let stringParams = '';
    // eslint-disable-next-line no-restricted-syntax
    for (const key in params) {
      if (stringParams === '') {
        stringParams += `${key}=${params[key]}`;
      } else {
        stringParams += `&${key}=${params[key]}`;
      }
    }
    return http
      .get(`benefits-reports/worker-enrolled?${stringParams}`, {
        headers: { companyId: params.companyId },
      })
      .then(resp => resp.data);
  }

  static getWorkerEnrolledBenefitsOld(params: IGetBenefitsWithParams): Promise<{
    items: WorkerEnrolledBenefitsType[];
    meta: { totalItems: number };
  }> {
    let stringParams = '';
    // eslint-disable-next-line no-restricted-syntax
    for (const key in params) {
      if (stringParams === '') {
        stringParams += `${key}=${params[key]}`;
      } else {
        stringParams += `&${key}=${params[key]}`;
      }
    }
    return http
      .get(`benefits-reports/worker-enrolled-old?${stringParams}`, {
        headers: { companyId: params.companyId },
      })
      .then(resp => resp.data);
  }

  static getWorkerEnrolledBenefitsNotEnrolled(params: IGetBenefitsWithParams): Promise<{
    items: WorkerEnrolledBenefitsType[];
    meta: { totalItems: number };
  }> {
    let stringParams = '';
    // eslint-disable-next-line no-restricted-syntax
    for (const key in params) {
      if (stringParams === '') {
        stringParams += `${key}=${params[key]}`;
      } else {
        stringParams += `&${key}=${params[key]}`;
      }
    }
    return http
      .get(`benefits-reports/worker-enrolled-notenrolled?${stringParams}`, {
        headers: { companyId: params.companyId },
      })
      .then(resp => resp.data);
  }

  static getWorkerEnrolledBenefitsXLSLink(
    eventType: string,
    startDate: string,
    endDate: string,
    copmanyId: string,
    search: string,
    employeeClass: string
  ) {
    return `${
      process.env.REACT_APP_API_URL
    }benefits-reports/export-enrolled-reports-xls?eventType=${eventType}&startDate=${startDate}&endDate=${endDate}&copmanyId=${copmanyId}&employeeClass=${employeeClass}&search=${search}&token=${getStorageValue(
      'accessToken',
      null
    )}`;
  }

  static getWorkerEnrolledBenefitsXLSLinkOld(
    eventType: string,
    startDate: string,
    endDate: string,
    copmanyId: string,
    search: string,
    employeeClass: string
  ) {
    return `${
      process.env.REACT_APP_API_URL
    }benefits-reports/export-enrolled-reports-xls-old?eventType=${eventType}&startDate=${startDate}&endDate=${endDate}&copmanyId=${copmanyId}&employeeClass=${employeeClass}&search=${search}&token=${getStorageValue(
      'accessToken',
      null
    )}`;
  }

  static getWorkerEnrolledBenefitsXLSLinkNotEnrolled(
    eventType: string,
    startDate: string,
    endDate: string,
    copmanyId: string,
    search: string,
    employeeClass: string
  ) {
    return `${
      process.env.REACT_APP_API_URL
    }benefits-reports/export-not-enrolled-reports-xls?eventType=${eventType}&startDate=${startDate}&endDate=${endDate}&copmanyId=${copmanyId}&employeeClass=${employeeClass}&search=${search}&token=${getStorageValue(
      'accessToken',
      null
    )}`;
  }

  static getBenefitsVersion(params: IGetBenefitsVersionWithParams): Promise<{
    data: { items: IBenefitsVersionManagement[]; meta: { totalItems: number } };
  }> {
    let stringParams = '';
    // eslint-disable-next-line no-restricted-syntax
    for (const key in params) {
      if (stringParams === '') {
        stringParams += `${key}=${params[key]}`;
      } else {
        stringParams += `&${key}=${params[key]}`;
      }
    }

    return http.get(`/benefits-maintain-version?${stringParams}`, {
      headers: { companyId: params.companyId },
    });
  }

  static editBenefitsVersion(data: IBenefitsVersionManagement) {
    return http
      .patch('/benefits-maintain-version', data)
      .then(response => response.data)
      .catch((err: Error | AxiosError) => {
        if (axios.isAxiosError(err)) {
          throw new Error(err.response?.data?.message);
        } else {
          return err;
        }
      });
  }

  static postBenefitFixedPrice(data: IBenefitFixedPrice) {
    return http
      .post('/benefits/prices/fixed-prices', data)
      .then(response => response.data)
      .catch((err: Error | AxiosError) => {
        if (axios.isAxiosError(err)) {
          throw new Error(err.response?.data?.message);
        } else {
          return err;
        }
      });
  }

  static patchBenefitFixedPrice(data: IBenefitFixedPrice) {
    return http
      .patch('/benefits/prices/fixed-prices', data)
      .then(response => response.data)
      .catch((err: Error | AxiosError) => {
        if (axios.isAxiosError(err)) {
          throw new Error(err.response?.data?.message);
        } else {
          return err;
        }
      });
  }

  static postBenefitAgePrice(data: IBenefitAgePrice) {
    return http
      .post('/benefits/prices/age-prices', data)
      .then(response => response.data)
      .catch((err: Error | AxiosError) => {
        if (axios.isAxiosError(err)) {
          throw new Error(err.response?.data?.message);
        } else {
          return err;
        }
      });
  }

  static patchBenefitAgePrice(data: IBenefitAgePrice) {
    return http
      .patch('/benefits/prices/age-prices', data)
      .then(response => response.data)
      .catch((err: Error | AxiosError) => {
        if (axios.isAxiosError(err)) {
          throw new Error(err.response?.data?.message);
        } else {
          return err;
        }
      });
  }

  static patchBenefitAgePriceLine(data: IBenefitAgePrice) {
    return http
      .patch(`/benefits/age-price-lines/${encodeURIComponent(data.type)}`, data.agePriceLines)
      .then(response => response.data)
      .catch((err: Error | AxiosError) => {
        if (axios.isAxiosError(err)) {
          throw new Error(err.response?.data?.message);
        } else {
          return err;
        }
      });
  }

  static getBenefitPremiumPayout(params: { versionId: string }): Promise<{
    data: IPremiumPayoutConfig;
  }> {
    return http.get(`/benefits/premium-configs/${params.versionId}`);
  }

  static postBenefitPremiumPayout(data: IPremiumPayoutConfig) {
    return http
      .post('/benefits/premium-configs', data)
      .then(response => response.data)
      .catch((err: Error | AxiosError) => {
        if (axios.isAxiosError(err)) {
          throw new Error(err.response?.data?.message);
        } else {
          return err;
        }
      });
  }

  static patchBenefitPremiumPayout(data: IPremiumPayoutConfig) {
    return http
      .patch('/benefits/premium-configs', data)
      .then(response => response.data)
      .catch((err: Error | AxiosError) => {
        if (axios.isAxiosError(err)) {
          throw new Error(err.response?.data?.message);
        } else {
          return err;
        }
      });
  }

  static getBenefitsConfigVersion(params: IGetBenefitsVersionWithParams): Promise<{
    data: { items: IBenefitsConfigVersion[]; meta: { totalItems: number } };
  }> {
    let stringParams = '';
    // eslint-disable-next-line no-restricted-syntax
    for (const key in params) {
      if (stringParams === '') {
        stringParams += `${key}=${params[key]}`;
      } else {
        stringParams += `&${key}=${params[key]}`;
      }
    }

    return http.get(`/benefits/config-version/${params.type}?${stringParams}`, {
      headers: { companyId: params.companyId },
    });
  }

  static postBenefitsConfigVersion(data: IBenefitsConfigVersion) {
    return http
      .post('/benefits/config-version', data)
      .then(response => response.data)
      .catch((err: Error | AxiosError) => {
        if (axios.isAxiosError(err)) {
          throw new Error(err.response?.data?.message);
        } else {
          return err;
        }
      });
  }

  static patchBenefitsConfigVersion(data: IBenefitsConfigVersion) {
    return http
      .patch(`/benefits/config-version/${data.id}`, data)
      .then(response => response.data)
      .catch((err: Error | AxiosError) => {
        if (axios.isAxiosError(err)) {
          throw new Error(err.response?.data?.message);
        } else {
          return err;
        }
      });
  }

  static duplicateBenefitsConfigVersion(id: string) {
    return http
      .post(`/benefits/config-version/${id}/duplicate`)
      .then(response => response.data)
      .catch((err: Error | AxiosError) => {
        if (axios.isAxiosError(err)) {
          throw new Error(err.response?.data?.message);
        } else {
          return err;
        }
      });
  }

  static duplicateBenefitsConfigVersionWithData(originalId: string, data: IBenefitsConfigVersion) {
    // Use the duplicate endpoint with custom data
    return http
      .post(`/benefits/config-version/${originalId}/duplicate`, data)
      .then(response => response.data)
      .catch((err: Error | AxiosError) => {
        if (axios.isAxiosError(err)) {
          throw new Error(err.response?.data?.message);
        } else {
          return err;
        }
      });
  }

  static getBenefitQleNewHirePriceSetup(): Promise<IQleNewHireEnrollmentEvent> {
    return http
      .get(`/benefits/qle-new-hire/price-setup`)
      .then(response => response.data)
      .catch((err: Error | AxiosError) => {
        if (axios.isAxiosError(err)) {
          throw new Error(err.response?.data?.message);
        } else {
          return err;
        }
      });
  }

  static putBenefitsQleNewHirePriceSetup(data: Partial<IQleNewHireEnrollmentEvent>) {
    return http
      .put(`/benefits/qle-new-hire/price-setup`, data)
      .then(response => response.data)
      .catch((err: Error | AxiosError) => {
        if (axios.isAxiosError(err)) {
          throw new Error(err.response?.data?.message);
        } else {
          return err;
        }
      });
  }
}
export default BenefitsService;
